/**
 * Performance Score Calculator
 *
 * Calculates a comprehensive performance score that combines multiple dimensions:
 * - Commit complexity score (technical contribution quality)
 * - Pull request count (delivery throughput)
 * - Code changes (lines added/removed)
 * - Issue resolution (problem-solving)
 * - Commit type distribution (work variety)
 *
 * The score is normalized to a 0-100 scale for easy interpretation.
 */

export interface PerformanceMetrics {
  // Commit metrics
  commitScore: number // Sum of complexity scores
  commitCount: number // Number of commits
  linesAdded: number // Total insertions
  linesRemoved: number // Total deletions
  filesChanged: number // Total files modified

  // PR metrics
  prCount: number // Merged PRs
  prAdditions: number // Lines added via PRs
  prDeletions: number // Lines removed via PRs

  // Issue metrics
  issuesResolved: number // Issues closed

  // Commit type distribution
  featureCommits: number
  bugfixCommits: number
  refactoringCommits: number
  otherCommits: number
}

export interface PerformanceScore {
  overall: number // 0-100 overall performance score
  breakdown: {
    quality: number // Code quality (complexity score)
    throughput: number // Delivery speed (PRs, commits)
    impact: number // Code impact (lines changed)
    problemSolving: number // Issue resolution
    versatility: number // Work variety
  }
  metrics: PerformanceMetrics
}

/**
 * Calculate a comprehensive performance score
 *
 * @param metrics - Raw performance metrics
 * @param timeRangeDays - Optional number of days in the time range for normalization (defaults to 30)
 * @returns Performance score with overall score and breakdown
 */
export function calculatePerformanceScore(
  metrics: PerformanceMetrics,
  timeRangeDays = 30,
): PerformanceScore {
  // Calculate individual component scores (0-100 scale)
  // All thresholds are normalized to a 30-day baseline, then adjusted for actual time range

  // Normalize factor: if time range is 7 days, thresholds should be 7/30 of the baseline
  const normalizeFactor = timeRangeDays / 30

  // 1. Quality Score (30% weight) - Based on commit complexity scores
  // Higher complexity scores indicate more challenging/valuable contributions
  const avgComplexityScore = metrics.commitCount > 0 ? metrics.commitScore / metrics.commitCount : 0
  const qualityScore = Math.min(100, (avgComplexityScore / 30) * 100) // Normalize around 30 points avg (adjusted from 50)

  // 2. Throughput Score (25% weight) - Based on merged PRs and commit frequency
  // Measures delivery speed and consistency (normalized to time range)
  const prThreshold = 6 * normalizeFactor // 6 PRs per 30 days baseline (adjusted from 10)
  const commitThreshold = 30 * normalizeFactor // 30 commits per 30 days baseline (adjusted from 50)
  const prScore = Math.min(100, (metrics.prCount / prThreshold) * 100)
  const commitFrequencyScore = Math.min(100, (metrics.commitCount / commitThreshold) * 100)
  const throughputScore = prScore * 0.6 + commitFrequencyScore * 0.4

  // 3. Impact Score (20% weight) - Based on lines of code changed
  // Measures the scale of contribution from both commits and PRs
  // Note: Previously only counted commit lines, causing contributors with PRs but no commits
  // to get 0 impact score. Now includes PR lines for accurate scoring (normalized to time range)
  const totalLinesChanged =
    metrics.linesAdded + metrics.linesRemoved + metrics.prAdditions + metrics.prDeletions
  const linesThreshold = 500 * normalizeFactor // 500 lines per 30 days baseline (adjusted from 1000)
  const impactScore = Math.min(100, (totalLinesChanged / linesThreshold) * 100)

  // 4. Problem Solving Score (15% weight) - Based on issues resolved
  // Measures proactive problem-solving (normalized to time range)
  const issuesThreshold = 3 * normalizeFactor // 3 issues per 30 days baseline (adjusted from 5)
  const problemSolvingScore = Math.min(100, (metrics.issuesResolved / issuesThreshold) * 100)

  // 5. Versatility Score (10% weight) - Based on commit type distribution
  // Measures breadth of contribution types
  const totalTypedCommits =
    metrics.featureCommits +
    metrics.bugfixCommits +
    metrics.refactoringCommits +
    metrics.otherCommits
  if (totalTypedCommits === 0) {
    // If no typed commits, use commit count as baseline
    const versatilityScore = metrics.commitCount > 0 ? 50 : 0

    const overall =
      qualityScore * 0.3 +
      throughputScore * 0.25 +
      impactScore * 0.2 +
      problemSolvingScore * 0.15 +
      versatilityScore * 0.1

    return {
      overall: Math.round(overall * 10) / 10, // Round to 1 decimal
      breakdown: {
        quality: Math.round(qualityScore * 10) / 10,
        throughput: Math.round(throughputScore * 10) / 10,
        impact: Math.round(impactScore * 10) / 10,
        problemSolving: Math.round(problemSolvingScore * 10) / 10,
        versatility: Math.round(versatilityScore * 10) / 10,
      },
      metrics,
    }
  }

  // Calculate Shannon diversity index for commit types
  const types = [
    metrics.featureCommits,
    metrics.bugfixCommits,
    metrics.refactoringCommits,
    metrics.otherCommits,
  ]
  const maxDiversity = Math.log(4) // Log of 4 types

  let diversity = 0
  for (const count of types) {
    if (count > 0) {
      const proportion = count / totalTypedCommits
      diversity -= proportion * Math.log(proportion)
    }
  }

  // Normalize diversity to 0-100 scale
  const versatilityScore = maxDiversity > 0 ? (diversity / maxDiversity) * 100 : 0

  // Calculate weighted overall score
  const overall =
    qualityScore * 0.3 +
    throughputScore * 0.25 +
    impactScore * 0.2 +
    problemSolvingScore * 0.15 +
    versatilityScore * 0.1

  return {
    overall: Math.round(overall * 10) / 10, // Round to 1 decimal
    breakdown: {
      quality: Math.round(qualityScore * 10) / 10,
      throughput: Math.round(throughputScore * 10) / 10,
      impact: Math.round(impactScore * 10) / 10,
      problemSolving: Math.round(problemSolvingScore * 10) / 10,
      versatility: Math.round(versatilityScore * 10) / 10,
    },
    metrics,
  }
}

/**
 * Calculate performance value as average of daily performance scores
 *
 * This calculates daily performance scores for the selected time range and returns
 * the average, excluding weekends (Saturday/Sunday) with zero activity.
 * Note: Uses yesterday as the end date to exclude the current incomplete day.
 *
 * @param commits - Array of commits with timestampDate
 * @param pullRequests - Array of pull requests with createdAtDate
 * @param issues - Array of issues with closedAtDate
 * @param dateRangeStart - Start date of the time range
 * @param dateRangeEnd - End date of the time range (defaults to yesterday at 23:59:59.999)
 * @returns Average daily performance score
 */
export function calculateAverageDailyPerformance(
  commits: Array<{
    score: number
    insertions: number
    deletions: number
    filesChanged: number
    commitType?: string
    timestampDate: Date
  }>,
  pullRequests: Array<{
    additions: number
    deletions: number
    state: string
    createdAtDate?: Date
    mergedAtDate?: Date
  }>,
  issues: Array<{
    state: string
    closedAtDate?: Date
  }>,
  dateRangeStart: Date,
  dateRangeEnd?: Date,
): number {
  // Use yesterday as the default end date if not provided
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(23, 59, 59, 999)
  const endDate = dateRangeEnd || yesterday

  const dailyScores: number[] = []
  const dayCount = Math.ceil((endDate.getTime() - dateRangeStart.getTime()) / (24 * 60 * 60 * 1000))

  for (let i = 0; i < dayCount; i++) {
    const dayStart = new Date(dateRangeStart)
    dayStart.setDate(dayStart.getDate() + i)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(dayStart)
    dayEnd.setHours(23, 59, 59, 999)

    // Get day of week (0 = Sunday, 6 = Saturday)
    const dayOfWeek = dayStart.getDay()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    // Filter data for this day
    const dayCommits = commits.filter(
      (c) => c.timestampDate >= dayStart && c.timestampDate <= dayEnd,
    )
    const dayPRs = pullRequests.filter((pr) => {
      const dateToCheck = pr.mergedAtDate || pr.createdAtDate
      return (
        dateToCheck && dateToCheck >= dayStart && dateToCheck <= dayEnd && pr.state === "merged"
      )
    })
    const dayIssues = issues.filter(
      (issue) =>
        issue.closedAtDate &&
        issue.closedAtDate >= dayStart &&
        issue.closedAtDate <= dayEnd &&
        issue.state === "closed",
    )

    // Calculate metrics for this day
    const dayMetrics = calculateMetrics(dayCommits, dayPRs, dayIssues)
    const dayScore = calculatePerformanceScore(dayMetrics, 1)

    // If it's a weekend and there's no activity, don't count it
    // Otherwise, include the day in the average
    const hasActivity = dayCommits.length > 0 || dayPRs.length > 0 || dayIssues.length > 0

    if (!isWeekend || hasActivity) {
      dailyScores.push(dayScore.overall)
    }
  }

  // Calculate average
  if (dailyScores.length === 0) {
    return 0
  }

  const sum = dailyScores.reduce((acc, score) => acc + score, 0)
  return sum / dailyScores.length
}

/**
 * Calculate performance metrics from raw data
 *
 * @param commits - Array of commits
 * @param pullRequests - Array of pull requests (merged)
 * @param issues - Array of issues (resolved)
 * @returns Performance metrics
 */
export function calculateMetrics(
  commits: Array<{
    score: number
    insertions: number
    deletions: number
    filesChanged: number
    commitType?: string
  }>,
  pullRequests: Array<{
    additions: number
    deletions: number
    state: string
  }>,
  issues: Array<{
    state: string
  }>,
): PerformanceMetrics {
  // Calculate commit metrics
  const commitScore = commits.reduce((sum, c) => sum + (c.score || 0), 0)
  const commitCount = commits.length
  const linesAdded = commits.reduce((sum, c) => sum + (c.insertions || 0), 0)
  const linesRemoved = commits.reduce((sum, c) => sum + (c.deletions || 0), 0)
  const filesChanged = commits.reduce((sum, c) => sum + (c.filesChanged || 0), 0)

  // Calculate PR metrics (only merged PRs)
  const mergedPRs = pullRequests.filter((pr) => pr.state === "merged")
  const prCount = mergedPRs.length
  const prAdditions = mergedPRs.reduce((sum, pr) => sum + (pr.additions || 0), 0)
  const prDeletions = mergedPRs.reduce((sum, pr) => sum + (pr.deletions || 0), 0)

  // Calculate issue metrics (only closed issues)
  const issuesResolved = issues.filter((issue) => issue.state === "closed").length

  // Calculate commit type distribution
  const featureCommits = commits.filter((c) => c.commitType === "feature").length
  const bugfixCommits = commits.filter((c) => c.commitType === "bugfix").length
  const refactoringCommits = commits.filter((c) => c.commitType === "refactoring").length
  const otherCommits = commits.filter(
    (c) =>
      c.commitType &&
      c.commitType !== "feature" &&
      c.commitType !== "bugfix" &&
      c.commitType !== "refactoring",
  ).length

  return {
    commitScore,
    commitCount,
    linesAdded,
    linesRemoved,
    filesChanged,
    prCount,
    prAdditions,
    prDeletions,
    issuesResolved,
    featureCommits,
    bugfixCommits,
    refactoringCommits,
    otherCommits,
  }
}
